
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { useEmail } from '@/context/EmailContext';
import { Email } from '@/context/email/types';

export const useEmailDetailLogic = () => {
  const { emailId, folder } = useParams<{ emailId: string, folder: string }>();
  const navigate = useNavigate();
  const { getEmail, markAsRead, loading, error } = useEmail();
  
  const [email, setEmail] = useState<Email | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(true); // Start with true to show skeleton
  const [emailNotFound, setEmailNotFound] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeType, setComposeType] = useState<'reply' | 'reply-all' | 'forward' | null>(null);
  
  // Use ref to track if we've already loaded this email
  const loadedEmailId = useRef<string | null>(null);
  const isLoadingRef = useRef(false);
  const hasAttemptedLoad = useRef(false);
  
  // Load email data with proper cleanup and deduplication
  useEffect(() => {
    if (!emailId) {
      console.log("📧 EmailDetailView: No email ID provided");
      setLoadingEmail(false);
      setEmailNotFound(true);
      return;
    }

    // If we already have this email loaded, don't reload
    // (loadedEmailId and the email state are always set together, and the
    // reset effect below clears both when the id changes)
    if (emailId === loadedEmailId.current) {
      console.log("📧 EmailDetailView: Email already loaded, skipping", emailId);
      setLoadingEmail(false);
      return;
    }

    // If we're already loading this email, don't start another request
    if (isLoadingRef.current && emailId === loadedEmailId.current) {
      console.log("📧 EmailDetailView: Already loading this email, skipping", emailId);
      return;
    }

    let isCancelled = false;
    
    const loadEmail = async () => {
      try {
        console.log("📧 EmailDetailView: Starting to load email with ID:", emailId, "in folder:", folder);
        isLoadingRef.current = true;
        hasAttemptedLoad.current = true;
        setLoadingEmail(true);
        setEmailNotFound(false);
        setEmail(null); // Clear previous email
        
        const fetchedEmail = await getEmail(emailId, folder || 'inbox');
        
        if (isCancelled) return;
        
        if (fetchedEmail) {
          console.log("📧 EmailDetailView: Email loaded successfully:", fetchedEmail.subject);
          setEmail(fetchedEmail);
          setEmailNotFound(false);
          loadedEmailId.current = emailId;
          
          // Mark as read if not already read and update local state
          if (!fetchedEmail.read) {
            markAsRead(emailId);
            // Update local email state to reflect read status immediately
            setEmail({ ...fetchedEmail, read: true });
          }
        } else {
          console.log("📧 EmailDetailView: Email not found or getEmail returned null");
          // Only set not found if we actually attempted to fetch and got null
          // getEmail might return null for duplicate requests, so we need to be careful
          if (hasAttemptedLoad.current) {
            setEmailNotFound(true);
            setEmail(null);
            loadedEmailId.current = null;
            toast.error("Email not found or has been deleted");
          }
        }
      } catch (err) {
        if (isCancelled) return;
        console.error('📧 EmailDetailView: Error loading email details:', err);
        setEmailNotFound(true);
        setEmail(null);
        loadedEmailId.current = null;
        toast.error("Failed to load email details");
      } finally {
        if (!isCancelled) {
          setLoadingEmail(false);
          isLoadingRef.current = false;
        }
      }
    };

    loadEmail();
    
    return () => {
      isCancelled = true;
    };
  // NOTE: `email` is intentionally NOT a dependency — including it re-fired
  // the effect mid-load, and the duplicate getEmail call used to return null,
  // producing a spurious "Email not found" toast alongside the loaded email.
  }, [emailId, folder, getEmail, markAsRead]);

  // Reset state when emailId changes
  useEffect(() => {
    if (emailId && emailId !== loadedEmailId.current) {
      console.log("📧 EmailDetailView: Email ID changed, resetting state", {
        newEmailId: emailId,
        previousEmailId: loadedEmailId.current
      });
      setEmail(null);
      setLoadingEmail(true);
      setEmailNotFound(false);
      loadedEmailId.current = null;
      isLoadingRef.current = false;
      hasAttemptedLoad.current = false;
    }
  }, [emailId]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return {
    email,
    loadingEmail,
    emailNotFound,
    showCompose,
    composeType,
    folder,
    loading,
    error,
    setShowCompose,
    setComposeType,
    handleBack
  };
};
